import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Validators, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { SessionService } from 'src/app/shared/services/session.service';
import { BaseComponent } from 'src/app/core/base.component';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { GlobalDataService } from 'src/app/shared/services/global-data.service';
import { RegistrationService } from '../registration.service';
import { RegistrationsList } from '../../registrations-list';
import { QueryFilter } from 'src/app/shared/models/query-filter.model';

export type AdminRegistrationMode = 'individual' | 'whole_team' | 'join_team';

@Component({
  selector: 'app-registration-create',
  templateUrl: './registration-create.component.html',
  styleUrls: ['./registration-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class RegistrationCreateComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  public canRegister = true;
  public multipleForm: FormGroup;
  public registrationMode: AdminRegistrationMode = 'individual';
  public selectedCaptainIndex = -1;
  public teamCaptains: { teamCaptain: string; numberOfGolfers: number }[] = [];
  public captainsLoading = false;
  public formError: string | null = null;
  private registrationSyncSub?: Subscription;
  private iterationSub?: Subscription;

  constructor(
    private registrationService: RegistrationService,
    stringHelpers: StringHelpers,
    public sessionService: SessionService,
    private globalDataService: GlobalDataService,
    private formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef
  ) {
    super(sessionService, stringHelpers);
  }

  ngOnInit(): void {
    this.multipleForm = this.formBuilder.group({
      teamCaptain: new FormControl<string>(''),
      registrations: this.formBuilder.array<FormGroup>([])
    });

    if (this.isGlobalOutingSet()) {
      this.selectedIteration = this.getGlobalOutingIteration();
      this.canRegister = this.checkIterationDate();
    }

    if (this.canRegister) {
      this.setRegistrationMode('individual');
      this.loadTeamCaptainsSummary();
    }
  }

  ngAfterViewInit(): void {
    this.iterationSub = this.globalDataService.iterationSelectionEvent.subscribe((result) => {
      this.selectedIteration = result;
      this.canRegister = this.checkIterationDate();
      if (this.canRegister) {
        this.setRegistrationMode(this.registrationMode);
        this.loadTeamCaptainsSummary();
      }
      this.cdRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.registrationSyncSub?.unsubscribe();
    this.iterationSub?.unsubscribe();
  }

  registrations(): FormArray {
    return this.multipleForm.get('registrations') as FormArray;
  }

  get hasJoinableTeam(): boolean {
    return this.teamCaptains.some((t) => t.numberOfGolfers < 4);
  }

  get allTeamsFull(): boolean {
    return this.teamCaptains.length > 0 && this.teamCaptains.every((t) => t.numberOfGolfers >= 4);
  }

  get canAddGolfer(): boolean {
    return this.registrationMode === 'whole_team' && this.registrations().length < 4;
  }

  get canRemoveGolfer(): boolean {
    return this.registrationMode === 'whole_team' && this.registrations().length > 2;
  }

  get submitDisabled(): boolean {
    if (this.multipleForm.invalid || this.registrations().length === 0) {
      return true;
    }
    if (this.registrationMode === 'join_team' && (!this.hasJoinableTeam || this.allTeamsFull)) {
      return true;
    }
    return false;
  }

  setRegistrationMode(mode: AdminRegistrationMode): void {
    this.registrationMode = mode;
    this.formError = null;
    this.selectedCaptainIndex = -1;
    this.registrationSyncSub?.unsubscribe();
    this.registrations().clear();
    const teamCaptain = this.multipleForm.get('teamCaptain');
    teamCaptain?.setValue('');
    teamCaptain?.clearValidators();

    if (mode === 'whole_team') {
      this.registrations().push(this.newRegistration());
      this.registrations().push(this.newRegistration());
    } else {
      this.registrations().push(this.newRegistration());
    }

    if (mode === 'join_team') {
      teamCaptain?.setValidators(Validators.required);
    }
    teamCaptain?.updateValueAndValidity();
    this.subscribeRegistrationSync();
    this.cdRef.markForCheck();
  }

  newRegistration(): FormGroup {
    return this.formBuilder.group({
      firstName: new FormControl('', { validators: Validators.required }),
      lastName: new FormControl('', { validators: Validators.required }),
      outingIterationId: this.selectedIteration.id,
      startingHole: '',
      isCheckedIn: false,
      hasPaid: false,
      createdBy: this.sessionService.getUserId()
    });
  }

  addRegistration(): void {
    if (!this.canAddGolfer) {
      return;
    }
    this.registrations().push(this.newRegistration());
    this.cdRef.markForCheck();
  }

  removeRegistration(index: number): void {
    if (!this.canRemoveGolfer) {
      return;
    }
    this.registrations().removeAt(index);
    if (this.selectedCaptainIndex === index) {
      this.selectedCaptainIndex = -1;
      this.multipleForm.get('teamCaptain')?.setValue('');
    } else if (this.selectedCaptainIndex > index) {
      this.selectedCaptainIndex -= 1;
    }
    this.syncWholeTeamCaptainFromRadio();
    this.cdRef.markForCheck();
  }

  onCaptainRadioChange(event: MatRadioChange): void {
    this.selectedCaptainIndex = event.value as number;
    this.syncWholeTeamCaptainFromRadio();
    this.cdRef.markForCheck();
  }

  trackTeamCaptainRow(_index: number, t: { teamCaptain: string; numberOfGolfers: number }): string {
    return t.teamCaptain;
  }

  golferNamesCompleteAt(index: number): boolean {
    const golfer = this.registrations().at(index) as FormGroup;
    if (!golfer) {
      return false;
    }
    const firstName = String(golfer.get('firstName')?.value ?? '').trim();
    const lastName = String(golfer.get('lastName')?.value ?? '').trim();
    return !!firstName && !!lastName;
  }

  private loadTeamCaptainsSummary(): void {
    if (!this.selectedIteration?.id) {
      this.teamCaptains = [];
      this.cdRef.markForCheck();
      return;
    }
    this.captainsLoading = true;
    this.teamCaptains = [];
    this.cdRef.markForCheck();
    const pageSize = 500;
    const base = new QueryFilter();
    base.page = 1;
    base.pageSize = pageSize;
    base.sortColumn = 'lastName';
    base.sortDirection = 'ASC';

    this.registrationService
      .getAllByIteration(this.selectedIteration.id, base)
      .pipe(
        switchMap((result: any) => {
          const rows: RegistrationsList[] = [...(result.results || [])];
          const total = result.totalCount ?? rows.length;
          const pagesNeeded = Math.min(Math.ceil(total / pageSize), 50);
          if (pagesNeeded <= 1) {
            return of(rows);
          }
          const rest = [];
          for (let p = 2; p <= pagesNeeded; p++) {
            const filter = new QueryFilter();
            filter.page = p;
            filter.pageSize = pageSize;
            filter.sortColumn = 'lastName';
            filter.sortDirection = 'ASC';
            rest.push(this.registrationService.getAllByIteration(this.selectedIteration.id, filter));
          }
          return forkJoin(rest).pipe(
            map((chunks: any[]) => {
              for (const c of chunks) {
                rows.push(...(c.results || []));
              }
              return rows;
            })
          );
        }),
        map((all: RegistrationsList[]) => this.aggregateTeamCaptains(all)),
        catchError(() => of([])),
        finalize(() => {
          this.captainsLoading = false;
          this.cdRef.markForCheck();
        })
      )
      .subscribe((captains) => {
        this.teamCaptains = captains;
        if (this.registrationMode === 'join_team' && captains.length === 0) {
          this.setRegistrationMode('individual');
        }
      });
  }

  private aggregateTeamCaptains(registrations: RegistrationsList[]): { teamCaptain: string; numberOfGolfers: number }[] {
    const counts = new Map<string, number>();
    for (const registration of registrations) {
      const name = (registration.teamCaptain || '').trim();
      if (!name) {
        continue;
      }
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([teamCaptain, numberOfGolfers]) => ({ teamCaptain, numberOfGolfers }))
      .sort((a, b) => a.teamCaptain.localeCompare(b.teamCaptain));
  }

  private subscribeRegistrationSync(): void {
    this.registrationSyncSub?.unsubscribe();
    if (this.registrationMode === 'individual') {
      const g = this.registrations().at(0) as FormGroup;
      if (g) {
        this.registrationSyncSub = g.valueChanges.subscribe(() => {
          this.applyIndividualCaptain();
          this.cdRef.markForCheck();
        });
      }
      this.applyIndividualCaptain();
    } else if (this.registrationMode === 'whole_team') {
      this.registrationSyncSub = this.registrations().valueChanges.subscribe(() => {
        this.syncWholeTeamCaptainFromRadio();
        this.cdRef.markForCheck();
      });
    }
  }

  private applyIndividualCaptain(): void {
    if (this.registrationMode !== 'individual') {
      return;
    }
    const g = this.registrations().at(0) as FormGroup;
    if (!g) {
      return;
    }
    const fn = String(g.get('firstName')?.value ?? '').trim();
    const ln = String(g.get('lastName')?.value ?? '').trim();
    if (fn && ln) {
      this.multipleForm.get('teamCaptain')?.setValue(`${fn} ${ln}`);
    } else {
      this.multipleForm.get('teamCaptain')?.setValue('');
    }
  }

  private syncWholeTeamCaptainFromRadio(): void {
    if (this.registrationMode !== 'whole_team' || this.selectedCaptainIndex < 0) {
      return;
    }
    const g = this.registrations().at(this.selectedCaptainIndex) as FormGroup;
    if (!g) {
      return;
    }
    const fn = String(g.get('firstName')?.value ?? '').trim();
    const ln = String(g.get('lastName')?.value ?? '').trim();
    if (fn && ln) {
      this.multipleForm.get('teamCaptain')?.setValue(`${fn} ${ln}`);
    } else {
      this.multipleForm.get('teamCaptain')?.setValue('');
    }
  }

  private validateBeforeSubmit(): boolean {
    this.formError = null;
    if (this.registrationMode === 'individual') {
      this.applyIndividualCaptain();
      const teamCaptain = this.multipleForm.get('teamCaptain')?.value;
      if (!teamCaptain || String(teamCaptain).trim() === '') {
        this.formError = 'Enter first and last name for the golfer.';
        return false;
      }
    }
    if (this.registrationMode === 'join_team') {
      if (!this.hasJoinableTeam || this.allTeamsFull) {
        this.formError = 'No team has an open spot for this outing.';
        return false;
      }
      const teamCaptain = this.multipleForm.get('teamCaptain')?.value;
      if (teamCaptain == null || String(teamCaptain).trim() === '') {
        this.formError = 'Select the team captain for the team you are joining.';
        return false;
      }
    }
    if (this.registrationMode === 'whole_team') {
      if (this.registrations().length < 2) {
        this.formError = 'Add at least two golfers for a team registration.';
        return false;
      }
      this.syncWholeTeamCaptainFromRadio();
      const teamCaptain = this.multipleForm.get('teamCaptain')?.value;
      if (this.selectedCaptainIndex < 0 || !teamCaptain || String(teamCaptain).trim() === '') {
        this.formError = 'Choose which golfer is the team captain.';
        return false;
      }
    }
    return true;
  }

  onSubmit(form: FormGroup): void {
    this.formError = null;
    if (!form.valid || !this.validateBeforeSubmit()) {
      this.cdRef.markForCheck();
      return;
    }
    this.isLoading = true;
    this.registrationService
      .createMultiple(form.value)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdRef.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.setRegistrationMode(this.registrationMode);
          this.loadTeamCaptainsSummary();
        },
        error: (err) => {
          const validationErrors = err.error;

          validationErrors.forEach(prop => {
            const registrations = form.get('registrations') as FormArray;

            const registrationGroup = registrations.controls.find(control =>
              control.get('id')?.value === prop.registrationId
            ) as FormGroup;
            
            if (registrationGroup) {
              const formControl = registrationGroup.get(
                this.stringHelpers.camelize(prop.propertyName)
              );
            
              if (formControl) {
                formControl.setErrors({
                  validationErrors: prop.errorMessage
                });
              }
            }
          });
        }
      });
  }
}
